import { number, bool, string, func, arrayOf, object } from 'prop-types';
import classnames from 'classnames';
import React, { PureComponent } from 'react';
import Card from '../../Card/Card';
import RangeChart from '../../RangeChart/RangeChart';
import InPlaceIndicator from '../../LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import { formatCurrencyIntl, translateSurveyStatus } from '../../lib/formatters';
import SeeMore from './SeeMore';
import LoadingIndicator from './LoadingIndicator';
import CompetitiveSetSelect from './CompetitiveSetSelect';
import CardFooter from './CardFooter';
import CommonErrorBoundary from '../../Util/CommonErrorBoundary';

export default class CrowdSourcedMatchCard extends PureComponent {
  static propTypes = {
    matchId: number,
    orgJobId: number,
    payRange: arrayOf(number),
    payRangeScaleMin: number,
    payRangeScaleMax: number,
    jobTasks: string,
    currentMatch: bool,
    className: string,
    loadingDetails: bool,
    jobCurrency: string,
    app: object,
    highVariance: bool,
    jobTitle: string,
    crowdSourceSurveyStatus: bool,
    surveyRow: object,
    jobDetails: object,
    saveProfilesAnalyzed: func,
    competitiveSetItems: arrayOf(object),
    jobSurveyActions: func,
    surveyDataLoading: bool,
    hasCompanySourceMatch: bool,
    showIncludeExclude: bool,
    typeOfMatch: string.isRequired,
    jobTaskMaxChar: number,
    onChangePayScaleMatchClick: func,
    readOnlyCard: bool,
    idText: string, // This prop is for distinguishing range charts, which need a unique ID to render, ex :'-modal'
    chartColors: arrayOf(string)
  };

  constructor(props) {
    super(props);
    this.bottomLabelFormatter = this.bottomLabelFormatter.bind(this);
  }

  bottomLabelFormatter(val, index) {
    const intl = {
      currency: this.props.jobCurrency,
      display: 'symbol',
      preferHourlyRate: false,
      useHourly: false,
      useThousands: true
    };

    return formatCurrencyIntl(val, intl, this.props.app);
  }

  render() {
    const {
      app,
      payRange,
      payRangeScaleMax,
      payRangeScaleMin,
      jobTasks,
      matchId,
      orgJobId,
      className,
      loadingDetails,
      highVariance,
      jobTitle,
      crowdSourceSurveyStatus,
      competitiveSetItems,
      surveyRow,
      jobSurveyActions,
      jobDetails,
      surveyDataLoading,
      hasCompanySourceMatch,
      showIncludeExclude,
      saveProfilesAnalyzed,
      typeOfMatch,
      jobTaskMaxChar,
      onChangePayScaleMatchClick,
      readOnlyCard,
      chartColors
    } = this.props;
    let translatedSurveyStatus = translateSurveyStatus(crowdSourceSurveyStatus);
    const currentMatchIncluded = translatedSurveyStatus === 'Included';
    let topRightCornerItem = <div className="current-match-card__change-match" />;

    if (typeOfMatch === 'jobMatch') {
      topRightCornerItem = (
        <div className="current-match-card__change-match">
          <button className="current-match-card__change-match-button" onClick={onChangePayScaleMatchClick}>
            Change Match
          </button>
        </div>
      );
    }

    let salaryTrendText = '';
    if (surveyRow && surveyRow.salary_trend_change) {
      const salaryTrendChange = ((surveyRow.salary_trend_change - 1) * 100).toFixed(1);
      let salaryTrendSymbol = '+';
      if (salaryTrendChange < 0) {
        salaryTrendSymbol = '';
      }
      salaryTrendText = salaryTrendSymbol + salaryTrendChange + '%';
    }

    if (typeOfMatch === 'surveyMatch' && !readOnlyCard) {
      topRightCornerItem = (
        <div className="current-match-card__change-match">
          <div className="current-match-card__salaryTrendDisplayPercent">{salaryTrendText}</div>
          <div className="current-match-card__salaryTrendDisplayInfo">
            <div>moved in</div>
            <div>last 3 months</div>
          </div>
        </div>
      );
    }

    return (
      <CommonErrorBoundary>
        <Card className={classnames({ [className]: className }, 'current-match-card')} raiseOnHover={false}>
          <LoadingIndicator loading={surveyDataLoading}>
            <div className={classnames({ excluded: !currentMatchIncluded }, 'current-match-card__content')}>
              <div className="current-match-card__title">
                <img className="current-match-card__logo" src="/view/static/mp/images/payScale-logo.png" />
                <div className="current-match-card__competitive-sets">
                  <CompetitiveSetSelect
                    competitiveSetItems={competitiveSetItems}
                    row={surveyRow}
                    actions={jobSurveyActions}
                    jobId={jobDetails.details.org_job_id}
                    disabled={!currentMatchIncluded}
                  />
                </div>
              </div>
              <div className="current-match-card__details">
                <div className="current-match-card__tasks-title">
                  <span>{jobTitle ? jobTitle + ' ' : 'Tasks for this job '}</span>
                  {typeOfMatch === 'surveyMatch' && !readOnlyCard && !surveyRow.surveyEditMode ? (
                    <button
                      className="current-match-card__edit-survey--link"
                      onClick={this.props.jobSurveyActions('editSurveyMode', 'get function').bind(this, surveyRow)}
                    >
                      Edit Survey
                    </button>
                  ) : null}
                </div>
                <div className="current-match-card__tasks-text">
                  <SeeMore maxChar={jobTaskMaxChar ? jobTaskMaxChar : 422}>{jobTasks}</SeeMore>
                </div>
                <div className="current-match-card__pay">
                  {loadingDetails && (
                    <div className="current-match-card__loading">
                      <InPlaceIndicator />
                    </div>
                  )}
                  {!loadingDetails && payRange && payRange.length > 0 && (
                    <RangeChart
                      className="match-range"
                      labelsVisible={true}
                      data={payRange}
                      height={25}
                      width={420}
                      scaleMax={payRangeScaleMax}
                      scaleMin={payRangeScaleMin}
                      barHeight={20}
                      bottomLabelFormatter={this.bottomLabelFormatter}
                      hideTopLabels
                      labelToBarSpacingTop={5}
                      labelTextAnchor={['start', 'middle', 'end']}
                      invertLabels={false}
                      labelFontSize={11}
                      colors={chartColors ? chartColors : ['#22d6dd', '#a7eff1']}
                      id={`match${matchId}-market-range${this.props.idText}`}
                      hasLine={!highVariance}
                      highVariance={highVariance}
                    />
                  )}
                  {!loadingDetails && (!payRange || payRange.length === 0) && (
                    <div className="current-match-card__no-data">
                      <i className="icon-warning" />
                      No salary data available
                    </div>
                  )}
                </div>
              </div>
              {topRightCornerItem}
            </div>
            <CardFooter
              app={app}
              typeOfMatch={typeOfMatch}
              isPayscaleMatch={true}
              surveyStatus={crowdSourceSurveyStatus}
              showIncludeExclude={showIncludeExclude}
              surveyRow={surveyRow}
              jobDetails={jobDetails}
              matchId={matchId}
              jobSurveyActions={jobSurveyActions}
              saveProfilesAnalyzed={saveProfilesAnalyzed}
              orgJobId={orgJobId}
              readOnlyCard={readOnlyCard}
            />
          </LoadingIndicator>
        </Card>
      </CommonErrorBoundary>
    );
  }
}
